import dayjs from 'dayjs';
import {
  isManufacturingRevenueServiceType,
  type RevenueServiceTypeOption,
} from './revenueServiceTypeRouting';

export type QuotationLineLike = {
  line_type?: string | null;
  rental_period_type?: string | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
  trading_rental_estimation_line_id?: string;
  rental_duration?: number | null;
  rental_rate?: number | null;
  quantity?: number | null;
  uom?: string | null;
  unit_price?: number | null;
  line_total?: number | null;
  net_amount?: number | null;
  vat_amount?: number | null;
  specifications?: string | null;
  security_deposit?: number | null;
  mobilization_charges?: number | null;
  demobilization_charges?: number | null;
  operator_charges?: number | null;
  transport_charges?: number | null;
  damage_charges?: number | null;
  discount_percentage?: number | null;
  tax_percentage?: number | null;
  vat_percentage?: number | null;
  vat_applicable?: boolean | null;
};

function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function hasRentalSpecificSignals(item: QuotationLineLike): boolean {
  return (
    num(item.rental_duration) > 0 ||
    Boolean(item.rental_period_type) ||
    Boolean(item.rental_start_date) ||
    Boolean(item.rental_end_date) ||
    rentalExtrasTotal(item) > 0
  );
}

export function isRentalQuotationItem(item: QuotationLineLike): boolean {
  const lineType = String(item.line_type || '').toLowerCase();
  if (lineType === 'rental') return true;
  if (lineType === 'trading') return false;
  return hasRentalSpecificSignals(item);
}

/** Use in form tables when rental columns are shown but line metadata is incomplete. */
export function showRentalFieldEditors(
  item: QuotationLineLike,
  options?: { quotationFromTradingRental?: boolean }
): boolean {
  if (isRentalQuotationItem(item)) return true;
  if (options?.quotationFromTradingRental && item.trading_rental_estimation_line_id) {
    return String(item.line_type || '').toLowerCase() !== 'trading';
  }
  return false;
}

export function hasAnyRentalQuotationLine(items: QuotationLineLike[] | undefined): boolean {
  return (items || []).some(isRentalQuotationItem);
}

export const RENTAL_PERIOD_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Custom', label: 'Custom' },
] as const;

export type QuotationPrintLayout = 'rental_only' | 'trading_only' | 'mixed';

/** Explicit duration wins; else derive from start/end dates and period type. */
export function deriveRentalDuration(line: QuotationLineLike): number {
  const explicit = num(line.rental_duration);
  if (explicit > 0) return explicit;
  if (!line.rental_start_date || !line.rental_end_date) return 0;
  const start = dayjs(line.rental_start_date);
  const end = dayjs(line.rental_end_date);
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return 0;
  const days = end.diff(start, 'day') + 1;
  const period = String(line.rental_period_type || 'Daily').toLowerCase();
  if (period === 'weekly') return Math.max(1, Math.ceil(days / 7));
  if (period === 'monthly') return Math.max(1, Math.ceil(days / 30));
  return Math.max(1, days);
}

export function getQuotationPrintLayout(items: QuotationLineLike[] | undefined): QuotationPrintLayout {
  const list = items || [];
  const rentalCount = list.filter(isRentalQuotationItem).length;
  const tradingCount = list.filter((i) => !isRentalQuotationItem(i)).length;
  if (rentalCount > 0 && tradingCount === 0) return 'rental_only';
  if (tradingCount > 0 && rentalCount === 0) return 'trading_only';
  return 'mixed';
}

export type QuotationDocumentKind = 'manufacturing' | 'trading_rental';

export type QuotationPrintTitle = 'QUOTATION';

export interface QuotationHeaderLike {
  trading_rental_estimation_id?: string | null;
  boq_id?: string | null;
  estimation_id?: string | null;
  revenue_service_type_id?: string | null;
  items?: QuotationLineLike[];
}

export interface QuotationPrintContext {
  documentKind: QuotationDocumentKind;
  layout: QuotationPrintLayout;
  title: QuotationPrintTitle;
  introText: string;
}

function isManufacturingQuotation(
  quotation: QuotationHeaderLike,
  revenueServiceType?: RevenueServiceTypeOption | null
): boolean {
  if (quotation.trading_rental_estimation_id) return false;
  if (isManufacturingRevenueServiceType(revenueServiceType)) return true;
  if (quotation.boq_id) return true;
  if (quotation.estimation_id) return true;
  return false;
}

export function resolveQuotationPrintContext(
  quotation: QuotationHeaderLike,
  revenueServiceType?: RevenueServiceTypeOption | null
): QuotationPrintContext {
  const manufacturing = isManufacturingQuotation(quotation, revenueServiceType);
  const layout = manufacturing ? 'trading_only' : getQuotationPrintLayout(quotation.items);

  const introText =
    layout === 'rental_only'
      ? 'As per your rental requirements please find below our best rates including period, duration, and applicable charges.'
      : 'As per your requirements please find below our best price for the said items. We do hope you will find our offer attractive and look forward to hearing from you at the earliest.';

  return {
    documentKind: manufacturing ? 'manufacturing' : 'trading_rental',
    layout,
    title: 'QUOTATION',
    introText,
  };
}

export function rentalLineRate(item: QuotationLineLike): number {
  return num(item.rental_rate) || num(item.unit_price);
}

export function rentalLineBaseAmount(item: QuotationLineLike): number {
  return num(item.quantity) * num(item.rental_duration) * rentalLineRate(item);
}

export type RentalCommercialBreakdown = {
  baseAmount: number;
  extrasTotal: number;
  discountAmount: number;
  netAmount: number;
  vatAmount: number;
  summaryLines: string[];
};

export function formatRentalLineCommercialBreakdown(item: QuotationLineLike): RentalCommercialBreakdown {
  const qty = num(item.quantity);
  const duration = num(item.rental_duration);
  const rate = rentalLineRate(item);
  const baseAmount = qty * duration * rate;
  const extrasTotal = rentalExtrasTotal(item);
  const totals = calculateRentalLineTotals(item);
  const discountPct = num(item.discount_percentage);
  const summaryLines: string[] = [];
  summaryLines.push(
    `Base: ${qty} × ${duration} × ${rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = ${baseAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  );
  if (extrasTotal > 0) {
    summaryLines.push(`Extras: ${extrasTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (discountPct > 0) {
    summaryLines.push(
      `Discount (${discountPct}%): ${totals.discount_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    );
  }
  summaryLines.push(
    `Net: ${totals.net_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  );
  if (totals.vat_amount > 0) {
    summaryLines.push(
      `VAT: ${totals.vat_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    );
  }
  return {
    baseAmount,
    extrasTotal,
    discountAmount: totals.discount_amount,
    netAmount: totals.net_amount,
    vatAmount: totals.vat_amount,
    summaryLines,
  };
}

export function buildRentalSpecificationsSummary(item: QuotationLineLike): string {
  const parts = [formatRentalQuantity(item), formatRentalPeriod(item)].filter(Boolean);
  const charges = formatRentalCharges(item);
  if (charges) parts.push(charges);
  return parts.join(' | ');
}

export function formatRentalQuantity(item: QuotationLineLike): string {
  const qty = num(item.quantity);
  const uom = (item.uom || 'PCS').trim();
  const duration = num(item.rental_duration);
  const period = String(item.rental_period_type || 'Daily').trim();
  if (duration > 0) {
    return `${qty} ${uom} × ${duration} ${period}`;
  }
  return `${qty} ${uom}`;
}

/** Date range only — for print/detail columns (Period + Duration are separate). */
export function formatRentalDatesOnly(item: QuotationLineLike): string {
  const start = item.rental_start_date ? dayjs(item.rental_start_date) : null;
  const end = item.rental_end_date ? dayjs(item.rental_end_date) : null;
  if (start?.isValid() && end?.isValid()) {
    return `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`;
  }
  if (start?.isValid()) {
    return `From ${start.format('DD MMM YYYY')}`;
  }
  if (end?.isValid()) {
    return `Until ${end.format('DD MMM YYYY')}`;
  }
  return '—';
}

export function formatRentalPeriod(item: QuotationLineLike): string {
  const datesOnly = formatRentalDatesOnly(item);
  if (datesOnly !== '—') return datesOnly;
  const duration = num(item.rental_duration);
  const period = String(item.rental_period_type || 'Daily').trim();
  if (duration > 0) {
    return `${duration} ${period}`;
  }
  return '—';
}

/** Hide auto-generated specifications on print (duplicates table columns). */
export function isRentalSpecificationsRedundant(item: QuotationLineLike): boolean {
  const spec = String(item.specifications || '').trim();
  if (!spec) return true;
  const auto = buildRentalSpecificationsSummary(item);
  if (spec === auto) return true;
  const qtyLine = formatRentalQuantity(item);
  if (spec.includes(qtyLine) && /duration/i.test(spec)) return true;
  return false;
}

export function rentalPrintNeedsBreakdown(item: QuotationLineLike): boolean {
  return (
    rentalExtrasTotal(item) > 0 ||
    num(item.discount_percentage) > 0
  );
}

export function formatRentalCharges(item: QuotationLineLike): string {
  const parts: { label: string; amount: number }[] = [
    { label: 'Security deposit', amount: num(item.security_deposit) },
    { label: 'Mobilization', amount: num(item.mobilization_charges) },
    { label: 'Demobilization', amount: num(item.demobilization_charges) },
    { label: 'Operator', amount: num(item.operator_charges) },
    { label: 'Transport', amount: num(item.transport_charges) },
    { label: 'Damage', amount: num(item.damage_charges) },
  ];
  const active = parts.filter((p) => p.amount > 0);
  if (active.length === 0) return '';
  return active.map((p) => `${p.label}: ${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join('; ');
}

export function rentalExtrasTotal(item: QuotationLineLike): number {
  return (
    num(item.security_deposit) +
    num(item.mobilization_charges) +
    num(item.demobilization_charges) +
    num(item.operator_charges) +
    num(item.transport_charges) +
    num(item.damage_charges)
  );
}

/** Match backend quotation.service rental line totals. */
export function calculateRentalLineTotals(item: QuotationLineLike): {
  net_amount: number;
  vat_amount: number;
  line_total: number;
  discount_amount: number;
} {
  const qty = num(item.quantity);
  const duration = num(item.rental_duration);
  const rate = num(item.rental_rate) || num(item.unit_price);
  const discountPct = num(item.discount_percentage);
  const taxPct = num(item.vat_percentage ?? item.tax_percentage);
  const base = qty * duration * rate;
  const beforeDiscount = base + rentalExtrasTotal(item);
  const discount_amount = (beforeDiscount * discountPct) / 100;
  const net_amount = beforeDiscount - discount_amount;
  const vat_amount = item.vat_applicable !== false ? (net_amount * taxPct) / 100 : 0;
  return {
    net_amount,
    vat_amount,
    line_total: net_amount,
    discount_amount,
  };
}

export function calculateTradingLineTotals(item: QuotationLineLike): {
  net_amount: number;
  vat_amount: number;
  line_total: number;
  discount_amount: number;
} {
  const qty = num(item.quantity);
  const unitPrice = num(item.unit_price);
  const discountPct = num(item.discount_percentage);
  const taxPct = num(item.vat_percentage ?? item.tax_percentage);
  const line_total = qty * unitPrice;
  const discount_amount = (line_total * discountPct) / 100;
  const net_amount = line_total - discount_amount;
  const vat_amount = item.vat_applicable !== false ? (net_amount * taxPct) / 100 : 0;
  return {
    net_amount,
    vat_amount,
    line_total,
    discount_amount,
  };
}

export function calculateQuotationLineTotals(item: QuotationLineLike): {
  net_amount: number;
  vat_amount: number;
  line_total: number;
  discount_amount: number;
} {
  if (isRentalQuotationItem(item)) {
    return calculateRentalLineTotals(item);
  }
  return calculateTradingLineTotals(item);
}

/** Prefer stored amounts; fall back to qty × price when legacy rows have net_amount 0. */
export function resolveQuotationLineNetAmount(item: QuotationLineLike): number {
  const stored = num(item.net_amount);
  if (stored > 0) return stored;
  const lineTotal = num(item.line_total);
  if (lineTotal > 0) return lineTotal;
  return calculateQuotationLineTotals(item).net_amount;
}

export function resolveQuotationLineVatAmount(item: QuotationLineLike): number {
  const stored = num(item.vat_amount);
  if (stored > 0.005) return stored;
  return calculateQuotationLineTotals(item).vat_amount;
}
